import { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
} from "react-native";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { apiRequest } from "../lib/api";
import { TimeSeriesChart } from "../../components/SignupsChart";
import { TopNBarChart } from "../../components/TopNBarChart";
import { PieChart } from "../../components/PieChart";
import Svg, { Line as SvgLine, Polyline, Circle as SvgCircle, Text as SvgText } from "react-native-svg";

interface Stats {
  totalUsers: number;
  totalDogs: number;
  totalRuns: number;
  totalBreeds: number;
  signupsThisWeek: number;
  signupsThisMonth: number;
  runsThisWeek: number;
}

interface UserRow {
  id: string;
  email: string;
  name: string;
  state: string | null;
  signup_source: string | null;
  created_at: string;
  dog_count: string;
  run_count: string;
}

interface DogRow {
  id: string;
  name: string;
  breed: string;
  registered_name: string | null;
  sex: string | null;
  owner_name: string;
  owner_email: string;
  run_count: string;
  best_speed: string | null;
  total_points: string | null;
}

interface Ga4Slice { key: string; value: number }
interface Ga4DayPoint { date: string; new: number; returning: number }
interface Ga4Data {
  configured: boolean;
  range?: { from: string; to: string };
  granularity?: "day" | "month";
  activeUsers?: number;
  newUsers?: number;
  returningUsers?: number;
  sessions?: number;
  byDay?: Ga4DayPoint[];
  sources?: Ga4Slice[];
  platforms?: Ga4Slice[];
  topDogProfiles?: Ga4Slice[];
}

type RangePreset = "thisMonth" | "lastMonth" | "last7" | "last30" | "last90" | "june" | "ytd";
function presetRange(p: RangePreset): { from: string; to: string; label: string } {
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const first = (y: number, m: number) => new Date(y, m, 1);
  const last = (y: number, m: number) => new Date(y, m + 1, 0);
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (p) {
    case "thisMonth": return { from: iso(first(y, m)), to: iso(today), label: "This Month" };
    case "lastMonth": return { from: iso(first(y, m - 1)), to: iso(last(y, m - 1)), label: "Last Month" };
    case "last7": return { from: iso(new Date(today.getTime() - 6 * 86400000)), to: iso(today), label: "Last 7 Days" };
    case "last30": return { from: iso(new Date(today.getTime() - 29 * 86400000)), to: iso(today), label: "Last 30 Days" };
    case "last90": return { from: iso(new Date(today.getTime() - 89 * 86400000)), to: iso(today), label: "Last 90 Days" };
    case "june": return { from: "2026-06-01", to: "2026-06-30", label: "June 2026" };
    case "ytd": return { from: iso(first(y, 0)), to: iso(today), label: `YTD ${y}` };
  }
}

function Ga4LineChart({ data, granularity }: { data: Ga4DayPoint[]; granularity: "day" | "month" }) {
  const [w, setW] = useState(0);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [show, setShow] = useState({ total: true, new: true, returning: true });
  const h = 240;
  const pL = 40, pR = 12, pT = 16, pB = 30;
  const plotW = Math.max(50, w - pL - pR);
  const plotH = h - pT - pB;
  if (data.length === 0) return <Text style={{ fontSize: 12, color: "#9ca3af", padding: 30, textAlign: "center" }}>No data in range</Text>;

  // Max only across visible series so hiding lines re-scales the y-axis
  const visibleMax = Math.max(
    1,
    ...data.flatMap((d) => [
      show.total ? d.new + d.returning : 0,
      show.new ? d.new : 0,
      show.returning ? d.returning : 0,
    ])
  );
  const xAt = (i: number) => pL + (data.length === 1 ? plotW / 2 : (i / (data.length - 1)) * plotW);
  const yAt = (v: number) => pT + plotH - (v / visibleMax) * plotH;
  const totalLine = data.map((d, i) => `${xAt(i)},${yAt(d.new + d.returning)}`).join(" ");
  const newLine = data.map((d, i) => `${xAt(i)},${yAt(d.new)}`).join(" ");
  const returningLine = data.map((d, i) => `${xAt(i)},${yAt(d.returning)}`).join(" ");

  const yGrid = 4;
  const labelFor = (s: string) => {
    if (granularity === "month") return s.slice(4) + "/" + s.slice(0, 4).slice(-2);
    return s.slice(4, 6) + "/" + s.slice(6, 8);
  };
  const longLabelFor = (s: string) => {
    if (granularity === "month") {
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return `${months[parseInt(s.slice(4, 6), 10) - 1]} ${s.slice(0, 4)}`;
    }
    return `${s.slice(4, 6)}/${s.slice(6, 8)}/${s.slice(0, 4)}`;
  };
  // Pick x-axis label indices by PIXEL spacing (not data length) so labels never
  // cram together on narrow/mobile widths. Guarantees first + last, and a minimum
  // gap between label centers; drops the prior label if it would collide with last.
  const minLabelGap = granularity === "month" ? 46 : 42;
  const xLabelIdxs: number[] = [];
  if (data.length > 0) {
    const lastI = data.length - 1;
    let lastX = -Infinity;
    for (let i = 0; i < lastI; i++) {
      const x = xAt(i);
      if (x - lastX >= minLabelGap) { xLabelIdxs.push(i); lastX = x; }
    }
    if (xLabelIdxs.length && xAt(lastI) - xAt(xLabelIdxs[xLabelIdxs.length - 1]) < minLabelGap) xLabelIdxs.pop();
    xLabelIdxs.push(lastI);
  }

  // Active point: whatever the user is scrubbing, else default to the latest so
  // the readout is never empty. The readout lives in a fixed bar ABOVE the chart
  // (so a finger never covers it on mobile) instead of a floating box that clips.
  const activeIdx = hoverIdx ?? data.length - 1;
  const active = data[activeIdx];
  const isLive = hoverIdx !== null;

  // Map an x-pixel (relative to the chart container) to the nearest data index.
  const pickAtX = (x: number) => {
    if (data.length <= 1) return setHoverIdx(0);
    const t = (x - pL) / plotW;
    const idx = Math.round(t * (data.length - 1));
    setHoverIdx(Math.max(0, Math.min(data.length - 1, idx)));
  };

  const series = [
    { key: "total" as const, label: "Total", color: "#1B2A4A", value: active ? active.new + active.returning : 0 },
    { key: "new" as const, label: "New", color: "#22C55E", value: active?.new ?? 0 },
    { key: "returning" as const, label: "Returning", color: "#F5A623", value: active?.returning ?? 0 },
  ].filter((s) => show[s.key]);

  return (
    <View onLayout={(e) => setW(e.nativeEvent.layout.width)} style={{ width: "100%" }}>
      {/* Readout bar — fixed above the chart, updates as you hover/scrub */}
      <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, minHeight: 34, marginBottom: 6 }}>
        <Text style={{ fontSize: 13, fontWeight: "800", color: "#1B2A4A", minWidth: 86 }}>
          {active ? longLabelFor(active.date) : ""}
        </Text>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, flex: 1 }}>
          {series.map((s) => (
            <View key={s.key} style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
              <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: s.color }} />
              <Text style={{ fontSize: 12, color: "#6b7280" }}>{s.label}</Text>
              <Text style={{ fontSize: 13, fontWeight: "800", color: "#111827" }}>{s.value.toLocaleString()}</Text>
            </View>
          ))}
        </View>
        {!isLive && (
          <Text style={{ fontSize: 10, color: "#9ca3af" }}>
            {Platform.OS === "web" ? "hover the chart" : "tap or drag the chart"}
          </Text>
        )}
      </View>

      {w > 0 && (
      <View style={{ position: "relative", width: w, height: h }}>
        <Svg width={w} height={h}>
          {Array.from({ length: yGrid + 1 }).map((_, i) => {
            const y = pT + (i / yGrid) * plotH;
            const v = Math.round(visibleMax - (i / yGrid) * visibleMax);
            return (
              <Svg key={i}>
                <SvgLine x1={pL} y1={y} x2={w - pR} y2={y} stroke="#f3f4f6" />
                <SvgText x={pL - 4} y={y + 3} textAnchor="end" fontSize={9} fill="#9ca3af">{v}</SvgText>
              </Svg>
            );
          })}
          {show.total && <Polyline points={totalLine} fill="none" stroke="#1B2A4A" strokeWidth={2.5} />}
          {show.new && <Polyline points={newLine} fill="none" stroke="#22C55E" strokeWidth={2} />}
          {show.returning && <Polyline points={returningLine} fill="none" stroke="#F5A623" strokeWidth={2} />}
          {xLabelIdxs.map((i) => (
            <SvgText key={`xl-${i}`} x={xAt(i)} y={h - 10} textAnchor="middle" fontSize={9} fill="#9ca3af">{labelFor(data[i].date)}</SvgText>
          ))}
          {/* Crosshair + dots at the active point */}
          {active && (
            <>
              <SvgLine x1={xAt(activeIdx)} y1={pT} x2={xAt(activeIdx)} y2={pT + plotH} stroke={isLive ? "#94a3b8" : "#e2e8f0"} strokeWidth={1} strokeDasharray="3,3" />
              {show.total && <SvgCircle cx={xAt(activeIdx)} cy={yAt(active.new + active.returning)} r={4} fill="#1B2A4A" stroke="#fff" strokeWidth={1.5} />}
              {show.new && <SvgCircle cx={xAt(activeIdx)} cy={yAt(active.new)} r={4} fill="#22C55E" stroke="#fff" strokeWidth={1.5} />}
              {show.returning && <SvgCircle cx={xAt(activeIdx)} cy={yAt(active.returning)} r={4} fill="#F5A623" stroke="#fff" strokeWidth={1.5} />}
            </>
          )}
        </Svg>
        {/* Full-area touch/mouse overlay — tap or drag anywhere to scrub */}
        <View
          style={{ position: "absolute", left: 0, top: 0, width: w, height: h }}
          onStartShouldSetResponder={() => true}
          onMoveShouldSetResponder={() => true}
          onResponderGrant={(e) => pickAtX(e.nativeEvent.locationX)}
          onResponderMove={(e) => pickAtX(e.nativeEvent.locationX)}
          {...(Platform.OS === "web" ? {
            onMouseMove: (e: any) => pickAtX(e.nativeEvent.offsetX),
            onMouseLeave: () => setHoverIdx(null),
          } as any : {})}
        />
      </View>
      )}
      <View style={{ flexDirection: "row", justifyContent: "center", gap: 14, marginTop: 8, flexWrap: "wrap" }}>
        {([
          { key: "total", label: "Total", color: "#1B2A4A" },
          { key: "new", label: "New", color: "#22C55E" },
          { key: "returning", label: "Returning", color: "#F5A623" },
        ] as const).map((s) => {
          const active = show[s.key];
          return (
            <TouchableOpacity
              key={s.key}
              onPress={() => setShow((prev) => ({ ...prev, [s.key]: !prev[s.key] }))}
              style={{ flexDirection: "row", alignItems: "center", gap: 4, opacity: active ? 1 : 0.35, paddingVertical: 4, paddingHorizontal: 6 }}
            >
              <View style={{ width: 12, height: 3, backgroundColor: s.color }} />
              <Text style={{ fontSize: 11, color: "#374151", textDecorationLine: active ? "none" : "line-through" }}>{s.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

async function adminFetch<T>(path: string): Promise<T> {
  return apiRequest<T>(path);
}

function StatCard({ label, value, icon, color }: { label: string; value: string | number; icon: string; color: string }) {
  return (
    <View style={{ backgroundColor: "#fff", borderRadius: 12, padding: 16, borderWidth: 1, borderColor: "#e5e7eb", flex: 1, minWidth: 140 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Ionicons name={icon as any} size={18} color={color} />
        <Text style={{ fontSize: 12, color: "#6b7280", fontWeight: "600", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</Text>
      </View>
      <Text style={{ fontSize: 28, fontWeight: "900", color: "#1B2A4A" }}>{value}</Text>
    </View>
  );
}

interface InsightsData {
  funnel: { signedUp: number; addedDog: number; hasRuns: number; returned: number };
  active: { dau: number; wau: number; mau: number };
  dataHealth: { dogsFull: number; dogsEstimate: number; dogsEmpty: number; usersNeedHelp: number };
}

function MiniStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <View style={{ flex: 1, backgroundColor: "#f8fafc", borderRadius: 10, padding: 12, borderWidth: 1, borderColor: "#eef2f7", minWidth: 90 }}>
      <Text style={{ fontSize: 22, fontWeight: "900", color }}>{value.toLocaleString()}</Text>
      <Text style={{ fontSize: 11, color: "#6b7280", fontWeight: "600", marginTop: 2 }}>{label}</Text>
    </View>
  );
}

function FunnelRow({ label, count, base, prev, color }: { label: string; count: number; base: number; prev: number | null; color: string }) {
  const pct = base > 0 ? (count / base) * 100 : 0;
  const step = prev != null && prev > 0 ? (count / prev) * 100 : null;
  return (
    <View style={{ marginBottom: 10 }}>
      <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <Text style={{ fontSize: 13, fontWeight: "700", color: "#1B2A4A" }}>{label}</Text>
        <Text style={{ fontSize: 12, color: "#6b7280" }}>
          <Text style={{ fontWeight: "800", color: "#111827" }}>{count.toLocaleString()}</Text>
          {"  "}{pct.toFixed(0)}%{step != null ? `  ·  ${step.toFixed(0)}% of prev step` : ""}
        </Text>
      </View>
      <View style={{ height: 20, backgroundColor: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
        <View style={{ width: `${Math.max(pct, 1.5)}%` as any, height: "100%", backgroundColor: color, borderRadius: 6 }} />
      </View>
    </View>
  );
}

function InsightsSection() {
  const [data, setData] = useState<InsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    adminFetch<InsightsData>("/dashadmin/insights")
      .then(setData)
      .catch((e) => console.warn("[admin] insights failed:", e))
      .finally(() => setLoading(false));
  }, []);

  const card = { backgroundColor: "#fff", borderRadius: 14, padding: 16, borderWidth: 1, borderColor: "#e5e7eb", marginBottom: 16 };
  if (loading) return <View style={card}><ActivityIndicator color="#3B82F6" /></View>;
  if (!data) return null;
  const f = data.funnel;

  return (
    <View style={card}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <Ionicons name="funnel" size={17} color="#1B2A4A" />
        <Text style={{ fontSize: 15, fontWeight: "800", color: "#1B2A4A" }}>Activation Funnel</Text>
      </View>
      <Text style={{ fontSize: 11, color: "#9ca3af", marginBottom: 12 }}>
        Where new users drop off — each bar is the share of all {f.signedUp.toLocaleString()} signups that reached that step.
      </Text>
      <FunnelRow label="Signed up" count={f.signedUp} base={f.signedUp} prev={null} color="#1B2A4A" />
      <FunnelRow label="Added a dog" count={f.addedDog} base={f.signedUp} prev={f.signedUp} color="#3B82F6" />
      <FunnelRow label="Has runs (activated)" count={f.hasRuns} base={f.signedUp} prev={f.addedDog} color="#10B981" />

      <View style={{ height: 1, backgroundColor: "#f1f5f9", marginVertical: 14 }} />
      <Text style={{ fontSize: 13, fontWeight: "800", color: "#1B2A4A", marginBottom: 2 }}>Retention</Text>
      <Text style={{ fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>Signed in again on a later day than they joined — they didn't sign up and vanish.</Text>
      <FunnelRow label="Came back later" count={f.returned} base={f.signedUp} prev={null} color="#22C55E" />

      <View style={{ height: 1, backgroundColor: "#f1f5f9", marginVertical: 14 }} />
      <Text style={{ fontSize: 13, fontWeight: "800", color: "#1B2A4A", marginBottom: 2 }}>Active Users</Text>
      <Text style={{ fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>People who opened the app (signed in) within each window.</Text>
      <View style={{ flexDirection: "row", gap: 10, flexWrap: "wrap" }}>
        <MiniStat label="Today" value={data.active.dau} color="#22C55E" />
        <MiniStat label="Last 7 days" value={data.active.wau} color="#3B82F6" />
        <MiniStat label="Last 30 days" value={data.active.mau} color="#1B2A4A" />
      </View>

      <View style={{ height: 1, backgroundColor: "#f1f5f9", marginVertical: 14 }} />
      <Text style={{ fontSize: 13, fontWeight: "800", color: "#1B2A4A", marginBottom: 2 }}>Run-History Health</Text>
      <Text style={{ fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>
        How complete dogs' histories are. "Estimate only" is your manual-load queue.
      </Text>
      <View style={{ flexDirection: "row", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <MiniStat label="Full history" value={data.dataHealth.dogsFull} color="#10B981" />
        <MiniStat label="Estimate only" value={data.dataHealth.dogsEstimate} color="#F5A623" />
        <MiniStat label="No runs yet" value={data.dataHealth.dogsEmpty} color="#9ca3af" />
      </View>
      {data.dataHealth.usersNeedHelp > 0 && (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, backgroundColor: "#FEF3C7", borderRadius: 8, padding: 10 }}>
          <Ionicons name="construct" size={16} color="#B45309" />
          <Text style={{ fontSize: 12, color: "#92400E", flex: 1 }}>
            <Text style={{ fontWeight: "800" }}>{data.dataHealth.usersNeedHelp}</Text> {data.dataHealth.usersNeedHelp === 1 ? "user has" : "users have"} an estimate but no loaded runs — candidates for a history backfill.
          </Text>
        </View>
      )}
    </View>
  );
}

export default function AdminDashboard() {
  const router = useRouter();
  const { width: screenWidth } = useWindowDimensions();
  const isWide = screenWidth >= 700;
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [dogs, setDogs] = useState<DogRow[]>([]);
  const [ga4, setGa4] = useState<Ga4Data | null>(null);
  const [ga4Loading, setGa4Loading] = useState(false);
  const [ga4Preset, setGa4Preset] = useState<RangePreset>("june");
  const [ga4Granularity, setGa4Granularity] = useState<"day" | "month">("day");
  const [tab, setTab] = useState<"users" | "dogs">("users");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) loadGa4();
  }, [ga4Preset, ga4Granularity]);

  async function loadGa4() {
    setGa4Loading(true);
    try {
      const r = presetRange(ga4Preset);
      const g = await adminFetch<Ga4Data>(`/dashadmin/ga4?from=${r.from}&to=${r.to}&granularity=${ga4Granularity}`);
      setGa4(g);
    } catch (e) {
      console.warn("[admin] GA4 load failed:", e);
    } finally {
      setGa4Loading(false);
    }
  }

  async function loadData() {
    try {
      const s = await adminFetch<Stats>("/dashadmin/stats");
      setStats(s);
      const u = await adminFetch<UserRow[]>("/dashadmin/users");
      setUsers(u);
      const d = await adminFetch<DogRow[]>("/dashadmin/dogs");
      setDogs(d);
      await loadGa4();
    } catch (err: any) {
      const msg = err?.message || "Unknown error";
      console.error("[admin] Load error:", msg, err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(d: string) {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color="#1B2A4A" />
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center", padding: 20 }}>
        <Ionicons name="lock-closed" size={48} color="#dc2626" />
        <Text style={{ color: "#dc2626", fontSize: 18, fontWeight: "700", marginTop: 16 }}>Error</Text>
        <Text style={{ color: "#6b7280", fontSize: 14, marginTop: 8, textAlign: "center" }}>{error}</Text>
        <TouchableOpacity onPress={() => loadData()} style={{ marginTop: 16, backgroundColor: "#F5A623", paddingHorizontal: 24, paddingVertical: 12, borderRadius: 10 }}>
          <Text style={{ color: "#fff", fontWeight: "600" }}>Retry</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => router.replace("/(tabs)" as any)} style={{ marginTop: 12, backgroundColor: "#1B2A4A", paddingHorizontal: 24, paddingVertical: 12, borderRadius: 10 }}>
          <Text style={{ color: "#fff", fontWeight: "600" }}>Go Back</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      {/* Header */}
      <View style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 16, paddingVertical: 14, backgroundColor: "#1B2A4A" }}>
        <TouchableOpacity onPress={() => router.replace("/(tabs)" as any)} style={{ marginRight: 12 }}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <Text style={{ fontSize: 20, fontWeight: "800", color: "#fff", flex: 1 }}>Dash-Hound Admin</Text>
        <Ionicons name="shield-checkmark" size={22} color="#F5A623" />
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {/* Stats grid */}
        {stats && (
          <>
            <View style={{ flexDirection: "row", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
              <StatCard label="Users" value={stats.totalUsers} icon="people" color="#3B82F6" />
              <StatCard label="Dogs" value={stats.totalDogs} icon="paw" color="#F5A623" />
            </View>
            <View style={{ flexDirection: "row", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
              <StatCard label="Total Runs" value={stats.totalRuns} icon="speedometer" color="#10B981" />
              <StatCard label="Breeds" value={stats.totalBreeds} icon="ribbon" color="#A855F7" />
            </View>
            <View style={{ flexDirection: "row", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
              <StatCard label="Signups (7d)" value={stats.signupsThisWeek} icon="trending-up" color="#22C55E" />
              <StatCard label="Signups (30d)" value={stats.signupsThisMonth} icon="calendar" color="#3B82F6" />
            </View>
          </>
        )}

        {/* Signups over time line chart */}
        <TimeSeriesChart title="Signups" endpoint="/dashadmin/signups" unit="signup" lineColor="#F5A623" />

        {/* Dogs added over time */}
        <TimeSeriesChart title="Dogs Added" endpoint="/dashadmin/dogs-added" unit="dog" lineColor="#3B82F6" />

        {/* Distribution charts — side by side, paginated */}
        <View style={{ flexDirection: "row", gap: 12, marginBottom: 12, marginTop: 16 }}>
          <TopNBarChart title="Users by State" endpoint="/dashadmin/users-by-state" barColor="#F5A623" paginated pageSize={10} />
          <TopNBarChart title="Dogs by Breed" endpoint="/dashadmin/dogs-by-breed" barColor="#3B82F6" paginated pageSize={10} />
        </View>

        {/* Signup source breakdown */}
        <View style={{ marginBottom: 16 }}>
          <TopNBarChart title="Users by Signup Source" endpoint="/dashadmin/users-by-source" barColor="#10B981" />
        </View>

        {/* GA4 Analytics */}
        {ga4 && ga4.configured && (
          <View style={{ backgroundColor: "#fff", borderRadius: 14, padding: 16, borderWidth: 1, borderColor: "#e5e7eb", marginBottom: 16 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Ionicons name="analytics" size={18} color="#3B82F6" />
              <Text style={{ fontSize: 15, fontWeight: "700", color: "#1B2A4A" }}>Google Analytics</Text>
              {ga4Loading && <ActivityIndicator size="small" color="#3B82F6" style={{ marginLeft: 8 }} />}
              <Text style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>
                {ga4.range?.from} → {ga4.range?.to === "today" ? "today" : ga4.range?.to}
              </Text>
            </View>

            {/* Range preset chips */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6, paddingBottom: 8 }} style={{ marginBottom: 8 }}>
              {(["june", "thisMonth", "lastMonth", "last7", "last30", "last90", "ytd"] as RangePreset[]).map((p) => {
                const selected = ga4Preset === p;
                return (
                  <TouchableOpacity key={p} onPress={() => setGa4Preset(p)} style={{ paddingVertical: 5, paddingHorizontal: 11, borderRadius: 14, backgroundColor: selected ? "#1B2A4A" : "#f3f4f6", borderWidth: 1, borderColor: selected ? "#1B2A4A" : "#e5e7eb" }}>
                    <Text style={{ fontSize: 11, fontWeight: "700", color: selected ? "#fff" : "#6b7280" }}>{presetRange(p).label}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>

            {/* Granularity toggle for the line chart */}
            <View style={{ flexDirection: "row", alignSelf: "flex-start", backgroundColor: "#f3f4f6", borderRadius: 8, padding: 2, marginBottom: 12 }}>
              {(["day", "month"] as const).map((g) => {
                const selected = ga4Granularity === g;
                return (
                  <TouchableOpacity key={g} onPress={() => setGa4Granularity(g)} style={{ paddingVertical: 5, paddingHorizontal: 12, borderRadius: 6, backgroundColor: selected ? "#1B2A4A" : "transparent" }}>
                    <Text style={{ fontSize: 11, fontWeight: "800", color: selected ? "#fff" : "#6b7280", letterSpacing: 0.4 }}>{g === "day" ? "DAILY" : "MONTHLY"}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Headline stat cards */}
            <View style={{ flexDirection: "row", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <StatCard label="Active Users" value={(ga4.activeUsers ?? 0).toLocaleString()} icon="people" color="#3B82F6" />
              <StatCard label="New Users" value={(ga4.newUsers ?? 0).toLocaleString()} icon="person-add" color="#22C55E" />
              <StatCard label="Returning" value={(ga4.returningUsers ?? 0).toLocaleString()} icon="repeat" color="#F5A623" />
            </View>

            {/* Line chart — users over time */}
            <View style={{ marginBottom: 16 }}>
              <Text style={{ fontSize: 12, fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Users Over Time</Text>
              <Ga4LineChart data={ga4.byDay || []} granularity={ga4Granularity} />
            </View>

            {/* Two-column pies */}
            <View style={{ flexDirection: isWide ? "row" : "column", gap: 16, marginBottom: 16 }}>
              <PieChart title="Traffic Sources" data={ga4.sources || []} />
              <PieChart title="Traffic by Platform" data={ga4.platforms || []} />
            </View>

            {/* Top dog profiles */}
            {ga4.topDogProfiles && ga4.topDogProfiles.length > 0 && (
              <View>
                <Text style={{ fontSize: 12, fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Top Dog Profiles</Text>
                {ga4.topDogProfiles.slice(0, 10).map((p, i) => {
                  const slug = p.key.replace(/^\/dog\//, "").replace(/\/$/, "");
                  return (
                    <View key={i} style={{ flexDirection: "row", paddingVertical: 6, borderBottomWidth: i < Math.min(9, ga4.topDogProfiles!.length - 1) ? 1 : 0, borderBottomColor: "#f3f4f6" }}>
                      <Text style={{ flex: 1, fontSize: 12, color: "#374151" }} numberOfLines={1}>{slug || "/"}</Text>
                      <Text style={{ fontSize: 12, fontWeight: "700", color: "#1B2A4A", marginLeft: 8 }}>{p.value.toLocaleString()}</Text>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        )}

        {/* Activation funnel + active users + run-history health */}
        <InsightsSection />

        {/* Tab switcher */}
        <View style={{ flexDirection: "row", gap: 8, marginBottom: 16 }}>
          <TouchableOpacity
            onPress={() => setTab("users")}
            style={{
              flex: 1,
              paddingVertical: 12,
              borderRadius: 10,
              backgroundColor: tab === "users" ? "#1B2A4A" : "#fff",
              borderWidth: 1,
              borderColor: tab === "users" ? "#1B2A4A" : "#d1d5db",
              alignItems: "center",
            }}
          >
            <Text style={{ fontWeight: "700", color: tab === "users" ? "#fff" : "#374151" }}>Users ({users.length})</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setTab("dogs")}
            style={{
              flex: 1,
              paddingVertical: 12,
              borderRadius: 10,
              backgroundColor: tab === "dogs" ? "#1B2A4A" : "#fff",
              borderWidth: 1,
              borderColor: tab === "dogs" ? "#1B2A4A" : "#d1d5db",
              alignItems: "center",
            }}
          >
            <Text style={{ fontWeight: "700", color: tab === "dogs" ? "#fff" : "#374151" }}>Dogs ({dogs.length})</Text>
          </TouchableOpacity>
        </View>

        {/* Users — table on wide, cards on mobile */}
        {tab === "users" && isWide && (
          <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden" }}>
            <View style={{ flexDirection: "row", backgroundColor: "#f9fafb", paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: "#e5e7eb" }}>
              <Text style={{ flex: 2, fontSize: 11, fontWeight: "700", color: "#6b7280" }}>USER</Text>
              <Text style={{ width: 50, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "center" }}>DOGS</Text>
              <Text style={{ width: 50, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "center" }}>RUNS</Text>
              <Text style={{ width: 50, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "center" }}>STATE</Text>
              <Text style={{ width: 60, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "center" }}>SOURCE</Text>
              <Text style={{ width: 80, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "right" }}>JOINED</Text>
            </View>
            {users.map((u, i) => (
              <TouchableOpacity
                key={u.id}
                onPress={() => router.push(`/dashadmin/user?id=${u.id}` as any)}
                style={{
                  flexDirection: "row",
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  backgroundColor: i % 2 === 0 ? "#fff" : "#f9fafb",
                  borderTopWidth: i > 0 ? 1 : 0,
                  borderTopColor: "#f3f4f6",
                  alignItems: "center",
                }}
              >
                <View style={{ flex: 2 }}>
                  <Text style={{ fontSize: 14, fontWeight: "600", color: "#1B2A4A" }} numberOfLines={1}>{u.name}</Text>
                  <Text style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }} numberOfLines={1}>{u.email}</Text>
                </View>
                <Text style={{ width: 50, fontSize: 14, fontWeight: "600", color: "#F5A623", textAlign: "center" }}>{u.dog_count}</Text>
                <Text style={{ width: 50, fontSize: 14, fontWeight: "600", color: "#10B981", textAlign: "center" }}>{u.run_count}</Text>
                <Text style={{ width: 50, fontSize: 13, fontWeight: "600", color: "#1B2A4A", textAlign: "center" }}>{u.state || "—"}</Text>
                <Text style={{ width: 60, fontSize: 11, fontWeight: "700", color: u.signup_source === "ios" ? "#1B2A4A" : u.signup_source === "android" ? "#10B981" : u.signup_source === "web" ? "#3B82F6" : "#9ca3af", textAlign: "center" }}>{u.signup_source ? u.signup_source.toUpperCase() : "—"}</Text>
                <Text style={{ width: 80, fontSize: 12, color: "#9ca3af", textAlign: "right" }}>{formatDate(u.created_at)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {tab === "users" && !isWide && (
          <View style={{ gap: 10 }}>
            {users.map((u) => {
              const sourceColor = u.signup_source === "ios" ? "#1B2A4A" : u.signup_source === "android" ? "#10B981" : u.signup_source === "web" ? "#3B82F6" : "#9ca3af";
              return (
                <TouchableOpacity
                  key={u.id}
                  onPress={() => router.push(`/dashadmin/user?id=${u.id}` as any)}
                  activeOpacity={0.7}
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: "#e5e7eb",
                    padding: 14,
                  }}
                >
                  {/* Name + source badge row */}
                  <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4 }}>
                    <Text style={{ flex: 1, fontSize: 16, fontWeight: "700", color: "#1B2A4A" }} numberOfLines={1}>
                      {u.name}
                    </Text>
                    {u.signup_source ? (
                      <View style={{ backgroundColor: sourceColor, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, marginLeft: 8 }}>
                        <Text style={{ fontSize: 10, fontWeight: "800", color: "#fff", letterSpacing: 0.5 }}>{u.signup_source.toUpperCase()}</Text>
                      </View>
                    ) : null}
                  </View>
                  {/* Email */}
                  <Text style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }} numberOfLines={1}>
                    {u.email}
                  </Text>
                  {/* Stats row */}
                  <View style={{ flexDirection: "row", alignItems: "center", paddingTop: 10, borderTopWidth: 1, borderTopColor: "#f3f4f6", gap: 14 }}>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                      <Ionicons name="paw" size={13} color="#F5A623" />
                      <Text style={{ fontSize: 13, fontWeight: "700", color: "#F5A623" }}>{u.dog_count}</Text>
                      <Text style={{ fontSize: 11, color: "#9ca3af" }}>dogs</Text>
                    </View>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                      <Ionicons name="flash" size={13} color="#10B981" />
                      <Text style={{ fontSize: 13, fontWeight: "700", color: "#10B981" }}>{u.run_count}</Text>
                      <Text style={{ fontSize: 11, color: "#9ca3af" }}>runs</Text>
                    </View>
                    {u.state ? (
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                        <Ionicons name="location" size={13} color="#1B2A4A" />
                        <Text style={{ fontSize: 13, fontWeight: "700", color: "#1B2A4A" }}>{u.state}</Text>
                      </View>
                    ) : null}
                    <Text style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af" }}>{formatDate(u.created_at)}</Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        {/* Dogs table */}
        {tab === "dogs" && (
          <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden" }}>
            {/* Header row */}
            <View style={{ flexDirection: "row", backgroundColor: "#f9fafb", paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: "#e5e7eb" }}>
              <Text style={{ flex: 2, fontSize: 11, fontWeight: "700", color: "#6b7280" }}>DOG</Text>
              <Text style={{ flex: 1, fontSize: 11, fontWeight: "700", color: "#6b7280" }}>OWNER</Text>
              <Text style={{ width: 45, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "center" }}>RUNS</Text>
              <Text style={{ width: 55, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "right" }}>MPH</Text>
            </View>
            {dogs.map((d, i) => (
              <View
                key={d.id}
                style={{
                  flexDirection: "row",
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  backgroundColor: i % 2 === 0 ? "#fff" : "#f9fafb",
                  borderTopWidth: i > 0 ? 1 : 0,
                  borderTopColor: "#f3f4f6",
                  alignItems: "center",
                }}
              >
                <View style={{ flex: 2 }}>
                  <Text style={{ fontSize: 14, fontWeight: "600", color: "#1B2A4A" }}>{d.name}</Text>
                  <Text style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{d.breed}{d.sex ? ` · ${d.sex}` : ""}</Text>
                </View>
                <Text style={{ flex: 1, fontSize: 12, color: "#6b7280" }} numberOfLines={1}>{d.owner_name}</Text>
                <Text style={{ width: 45, fontSize: 14, fontWeight: "600", color: "#10B981", textAlign: "center" }}>{d.run_count}</Text>
                <Text style={{ width: 55, fontSize: 14, fontWeight: "600", color: "#F5A623", textAlign: "right" }}>
                  {d.best_speed ? parseFloat(d.best_speed).toFixed(1) : "—"}
                </Text>
              </View>
            ))}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
